import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface GoogleOneTapProps {
  onSuccess?: () => void;
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ onSuccess }) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't show One Tap if user is already logged in
    if (user) {
      console.log('[GoogleOneTap] User already logged in, skipping');
      return;
    }

    console.log('[GoogleOneTap] Initializing...');
    
    // Function to load the Google SDK script dynamically if not present
    const loadGoogleScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Check if already loaded
        if (typeof window.google !== 'undefined') {
          console.log('[GoogleOneTap] Google SDK already loaded');
          resolve();
          return;
        }
        
        // Check if script tag exists
        const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existingScript) {
          console.log('[GoogleOneTap] Script tag exists, waiting for load...');
          existingScript.addEventListener('load', () => {
            console.log('[GoogleOneTap] Script loaded via existing tag');
            resolve();
          });
          existingScript.addEventListener('error', (error) => {
            console.error('[GoogleOneTap] Script failed to load:', error);
            reject(error);
          });
          return;
        }
        
        // Create and load script dynamically
        console.log('[GoogleOneTap] Creating script tag dynamically...');
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          console.log('[GoogleOneTap] Script loaded dynamically');
          resolve();
        };
        
        script.onerror = (error) => {
          console.error('[GoogleOneTap] Failed to load script dynamically:', error);
          reject(error);
        };
        
        document.head.appendChild(script);
      });
    };

    // Initialize Google One Tap
    const initializeGoogleOneTap = () => {
      if (typeof window.google !== 'undefined') {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        
        console.log('[GoogleOneTap] Google SDK loaded');
        console.log('[GoogleOneTap] Client ID configured:', clientId ? 'Yes' : 'No');
        
        if (!clientId) {
          console.error('[GoogleOneTap] REACT_APP_GOOGLE_CLIENT_ID not configured');
          return;
        }

        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
            auto_select: true, // Automatically sign in returning users
            cancel_on_tap_outside: true,
            context: 'signin',
          });

          console.log('[GoogleOneTap] Initialized, displaying prompt...');

          // Display the One Tap prompt
          window.google.accounts.id.prompt((notification) => {
            console.log('[GoogleOneTap] Prompt notification:', {
              isDisplayed: notification.isDisplayed(),
              isNotDisplayed: notification.isNotDisplayed(),
              isSkippedMoment: notification.isSkippedMoment(),
              isDismissedMoment: notification.isDismissedMoment(),
              momentType: notification.getMomentType(),
            });
            
            if (notification.isNotDisplayed()) {
              console.log('[GoogleOneTap] Not displayed. Reason:', notification.getNotDisplayedReason());
            }
            
            if (notification.isSkippedMoment()) {
              console.log('[GoogleOneTap] Skipped. Reason:', notification.getSkippedReason());
            }
            
            if (notification.isDismissedMoment()) {
              console.log('[GoogleOneTap] Dismissed. Reason:', notification.getDismissedReason());
            }
          });
        } catch (error) {
          console.error('[GoogleOneTap] Initialization error:', error);
        }
      }
    };

    const handleCredentialResponse = async (response: any) => {
      console.log('[GoogleOneTap] Credential received');
      
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        
        console.log('[GoogleOneTap] Sending credential to backend:', backendUrl);
        
        // Send the credential token to your backend
        const res = await fetch(`${backendUrl}/api/auth/google/one-tap`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            credential: response.credential,
          }),
        });

        const data = await res.json();
        
        console.log('[GoogleOneTap] Backend response:', { ok: res.ok, status: res.status });

        if (res.ok && data.access_token) {
          console.log('[GoogleOneTap] Authentication successful');
          
          // Store the tokens with correct key names (matching authService)
          localStorage.setItem('access_token', data.access_token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          
          // Store the user data
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          
          // Update auth context
          if (updateUser && data.user) {
            updateUser(data.user);
          }

          toast.success('Login realizado com sucesso!');
          onSuccess?.();
          
          // Small delay to ensure auth state is updated before navigation
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check for redirect
          const redirect = localStorage.getItem('oauthRedirect');
          if (redirect) {
            localStorage.removeItem('oauthRedirect');
            navigate(redirect);
          } else {
            navigate('/home');
          }
        } else {
          console.error('[GoogleOneTap] Authentication failed:', data);
          throw new Error(data.message || 'Falha ao fazer login');
        }
      } catch (error) {
        console.error('[GoogleOneTap] Error:', error);
        toast.error('Erro ao fazer login com Google One Tap');
      }
    };

    // Load the Google script and initialize
    loadGoogleScript()
      .then(() => {
        console.log('[GoogleOneTap] Script loaded successfully, initializing...');
        // Small delay to ensure SDK is fully initialized
        setTimeout(() => {
          if (typeof window.google !== 'undefined') {
            initializeGoogleOneTap();
          } else {
            console.error('[GoogleOneTap] window.google still undefined after script load');
          }
        }, 100);
      })
      .catch((error) => {
        console.error('[GoogleOneTap] Failed to load Google SDK:', error);
      });

    return () => {
      console.log('[GoogleOneTap] Component unmounting, cleaning up');
    };
  }, [user, updateUser, navigate, onSuccess]);

  return null; // This component doesn't render anything visible
};

export default GoogleOneTap;
